import React from "react";
import TableCell from "@mui/material/TableCell/TableCell";
import EditIcon from "@mui/icons-material/Edit";
import { Tooltip } from "@mui/material";
import Button from "../../Components/Button";
import CheckBox from "../../Components/Checkbox";
import Modal from "../../Components/Modal/Modal";
import TextInput from "../../Components/TextInput";
import AddOrEditSahraUser from "../../Forms/AddOrEditSahraUser";
import {
  ISahraUserGroupsByAccountReport,
  ISahraUserGroupsByAccountSearchParam,
} from "../../Interfaces/sahraUserGroupsInterfaces";
import { getSahraUserGroupsByAccountTable } from "../../Requests/sahraUserGroupRequests";
import { CommonTableUI } from "../TableTemplate/common";
import TableTemplate, { IAdditional } from "../TableTemplate/tableTemplate";
import { useDebouncer } from "../../Hooks/useDebouncer";

const AdditionalHeader = () => <TableCell />;

const AdditionalRow: React.FC<{
  row: ISahraUserGroupsByAccountReport;
  onAction: () => void;
}> = ({ row, onAction }) => {
  const [isEditOpen, setIsEditOpen] = React.useState(false);

  return (
    <TableCell>
      {isEditOpen ? (
        <Modal title="ویرایش کاربر صحرا"
          UI={
            <AddOrEditSahraUser
              userId={row.id}
              onClose={() => {
                setIsEditOpen(false);
                onAction();
              }}
            />
          }
          onClose={() => setIsEditOpen(false)}
          modalBoxClassName="!min-w-[300px]"
        />
      ) : null}
      <Tooltip title="ویرایش" arrow>
        <Button
          className="table-btn"
          onClick={() => setIsEditOpen(true)}
          text={<EditIcon className="!text-sm" />}
          variant="contained"
        />
      </Tooltip>
    </TableCell>
  );
};

const AccountsTableAdditional: IAdditional<ISahraUserGroupsByAccountReport> = {
  getHeader: AdditionalHeader,
  getRow: AdditionalRow,
};

const initialSearchParameters: ISahraUserGroupsByAccountSearchParam = {
  justActive: true,
};

const AccountsTable = () => {
  const [searchParameters, setSearchParameters] =
    React.useState<ISahraUserGroupsByAccountSearchParam>(
      initialSearchParameters,
    );
  const [nameInput, setNameInput] = React.useState("");
  const debouncedName = useDebouncer(nameInput, 300);

  React.useEffect(() => {
    setSearchParameters((current) => ({
      ...current,
      name: debouncedName,
    }));
  }, [debouncedName]);

  const changeSearchParameter = (name: string, value: boolean) => {
    setSearchParameters((current) => ({ ...current, [name]: value }));
  };

  return (
    <div
      dir="rtl"
      className="flex w-full flex-col items-start gap-3 [&_[aria-busy=true]]:min-w-[min(960px,calc(100vw-8rem))]"
    >
      <div className="flex w-fit flex-row items-center justify-start gap-2">
        <TextInput
          label="نام اکانت"
          name="name"
          onChange={(_, value) => setNameInput(value)}
          value={nameInput}
          autoComplete="off"
        />
        <CheckBox
          checked={searchParameters.justActive ?? true}
          label="فقط فعال"
          name="justActive"
          onChange={(name, value) =>
            setSearchParameters((current) => ({
              ...current,
              [name]: value,
            }))
          }
        />
      </div>
      <TableTemplate<
        ISahraUserGroupsByAccountSearchParam,
        ISahraUserGroupsByAccountReport
      >
        getDataTable={getSahraUserGroupsByAccountTable}
        initialSearchParameters={initialSearchParameters}
        searchParameters={searchParameters}
        tableWrapperClassname="!w-fit"
        TableUI={({ reports, onAction }) => (
          <CommonTableUI
            reports={reports}
            onAction={onAction}
            AdditionalBody={AccountsTableAdditional}
            tableBoxClassName="max-h-[calc(100dvh-230px)] overflow-y-auto"
          />
        )}
      />
    </div>
  );
};

export default AccountsTable;
